import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import api from "../../../../../services/api";
import type { Menu, MenuItem, MenuPage, SaveStatus } from "../types";

const menuKey = (menuId: string) => ["menu-v2", menuId] as const;

interface MenuResponse {
  menu?: Menu;
  // Some endpoints return the menu directly
  [k: string]: unknown;
}

const extractMenu = (data: MenuResponse | Menu): Menu => {
  return (data as MenuResponse).menu
    ? ((data as MenuResponse).menu as Menu)
    : (data as Menu);
};

const normalizeCategoryList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return Array.from(
      new Set(
        value
          .map((entry) =>
            typeof entry === "string" ? entry.trim() : String(entry ?? "").trim()
          )
          .filter(Boolean)
      )
    );
  }
  if (typeof value === "string" && value.trim()) return [value.trim()];
  return [];
};

const getItemCategories = (item: MenuItem): string[] => {
  const arr = normalizeCategoryList((item as any).categories);
  if (arr.length) return arr;
  return normalizeCategoryList(item.category);
};

const getItemEnglishCategories = (item: MenuItem): string[] => {
  const props = (item as any)?.props || {};
  const list = normalizeCategoryList(props?.translations?.en?.categoryList);
  if (list.length) return list;
  const text =
    props?.translations?.en?.category?.text || props?.enCategory?.text;
  if (text) return normalizeCategoryList(text);
  return [];
};

/**
 * Read categoryOrder regardless of whether it's stored as a flat string[]
 * or as [[name, idx], ...] tuples (the legacy / canonical format).
 * Returns an ordered list of category names.
 */
const readCategoryOrder = (value: unknown): string[] => {
  if (!Array.isArray(value)) return [];
  // Tuple format: [[name, idx], ...]
  if (value.length > 0 && Array.isArray(value[0])) {
    return (value as Array<[string, number]>)
      .slice()
      .sort((a, b) => (a[1] ?? 0) - (b[1] ?? 0))
      .map(([name]) => name);
  }
  // Flat string format
  return normalizeCategoryList(value);
};

/** Persist a flat ordered list back to the tuple format used by the public menu. */
const toCategoryOrderTuples = (
  ordered: string[]
): Array<[string, number]> => ordered.map((name, idx) => [name, idx]);

/**
 * Central data hook for the v2 menu editor.
 *
 * Strategy:
 * - One useQuery fetches the menu + nested pages + items.
 * - Mutations PATCH the cache (queryClient.setQueryData) instead of refetching,
 *   so updating one item doesn't re-render every other row.
 */
export function useMenuV2(menuId: string) {
  const queryClient = useQueryClient();
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const pendingSavesRef = useRef(0);

  const query = useQuery(
    menuKey(menuId),
    async () => {
      const res = await api.get(`/menus/${menuId}`);
      return extractMenu(res.data);
    },
    {
      enabled: !!menuId,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const patchMenu = useCallback(
    (updater: (prev: Menu) => Menu) => {
      queryClient.setQueryData<Menu | undefined>(menuKey(menuId), (prev) => {
        if (!prev) return prev;
        return updater(prev);
      });
    },
    [menuId, queryClient]
  );

  const trackSave = useCallback(
    async <T,>(work: () => Promise<T>): Promise<T> => {
      pendingSavesRef.current += 1;
      setSaveStatus("saving");
      try {
        const result = await work();
        pendingSavesRef.current -= 1;
        if (pendingSavesRef.current <= 0) {
          pendingSavesRef.current = 0;
          setSaveStatus("saved");
          setLastSavedAt(new Date());
        }
        return result;
      } catch (e) {
        pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
        setSaveStatus("error");
        throw e;
      }
    },
    []
  );

  // ---------- Menu-level updates (notes, images, categoryOrder, drinkSection, onboarding) ----------

  const updateMenu = useCallback(
    async (updates: Partial<Menu>) => {
      // Optimistic patch
      const prev = queryClient.getQueryData<Menu>(menuKey(menuId));
      patchMenu((m) => ({ ...m, ...updates }));
      try {
        await trackSave(() => api.put(`/menus/${menuId}`, updates));
      } catch (e) {
        // Roll back on failure
        if (prev) queryClient.setQueryData(menuKey(menuId), prev);
        throw e;
      }
    },
    [menuId, patchMenu, queryClient, trackSave]
  );

  // ---------- Item-level updates ----------

  const updateItem = useCallback(
    async (itemId: string, updates: Partial<MenuItem>) => {
      const prev = queryClient.getQueryData<Menu>(menuKey(menuId));
      patchMenu((m) => ({
        ...m,
        menuPages: (m.menuPages || []).map((p) => ({
          ...p,
          menuItems: (p.menuItems || []).map((it) =>
            it.id === itemId ? { ...it, ...updates } : it
          ),
        })),
      }));
      try {
        const res = await trackSave(() =>
          api.put(`/menu-items/${itemId}`, updates)
        );
        // Merge canonical server response in case server normalized fields
        const serverItem: MenuItem | undefined =
          (res.data as any)?.menuItem || (res.data as any);
        if (serverItem) {
          patchMenu((m) => ({
            ...m,
            menuPages: (m.menuPages || []).map((p) => ({
              ...p,
              menuItems: (p.menuItems || []).map((it) =>
                it.id === itemId ? { ...it, ...serverItem } : it
              ),
            })),
          }));
        }
      } catch (e) {
        if (prev) queryClient.setQueryData(menuKey(menuId), prev);
        throw e;
      }
    },
    [menuId, patchMenu, queryClient, trackSave]
  );

  const deleteItem = useCallback(
    async (itemId: string) => {
      const prev = queryClient.getQueryData<Menu>(menuKey(menuId));
      patchMenu((m) => ({
        ...m,
        menuPages: (m.menuPages || []).map((p) => ({
          ...p,
          menuItems: (p.menuItems || []).filter((it) => it.id !== itemId),
        })),
      }));
      try {
        await trackSave(() => api.delete(`/menu-items/${itemId}`));
      } catch (e) {
        if (prev) queryClient.setQueryData(menuKey(menuId), prev);
        throw e;
      }
    },
    [menuId, patchMenu, queryClient, trackSave]
  );

  const createItem = useCallback(
    async (pageId: string, data: Partial<MenuItem>) => {
      const res = await trackSave(() =>
        api.post(`/menu-items`, { ...data, menuPageId: pageId })
      );
      const created: MenuItem =
        (res.data as any)?.menuItem || (res.data as any);
      patchMenu((m) => ({
        ...m,
        menuPages: (m.menuPages || []).map((p) =>
          p.id === pageId
            ? { ...p, menuItems: [...(p.menuItems || []), created] }
            : p
        ),
      }));
      return created;
    },
    [patchMenu, trackSave]
  );

  // ---------- Page-level updates ----------

  const updatePage = useCallback(
    async (pageId: string, updates: Partial<MenuPage>) => {
      const prev = queryClient.getQueryData<Menu>(menuKey(menuId));
      patchMenu((m) => ({
        ...m,
        menuPages: (m.menuPages || []).map((p) =>
          p.id === pageId ? { ...p, ...updates } : p
        ),
      }));
      try {
        await trackSave(() => api.put(`/menu-pages/${pageId}`, updates));
      } catch (e) {
        if (prev) queryClient.setQueryData(menuKey(menuId), prev);
        throw e;
      }
    },
    [menuId, patchMenu, queryClient, trackSave]
  );

  // ---------- Category operations ----------

  /**
   * Rename a category. Affects both source (item.categories / item.category)
   * and English (props.translations.en.categoryList / .category.text) where the
   * old name appears — mirroring the backend rename endpoint's behavior.
   */
  const renameCategory = useCallback(
    async (oldName: string, newName: string) => {
      const trimmed = newName.trim();
      if (!trimmed || trimmed === oldName) return;
      const prev = queryClient.getQueryData<Menu>(menuKey(menuId));

      patchMenu((m) => {
        // Rewrite categoryOrder (handle tuple OR flat formats; emit tuple format)
        const orderNames = readCategoryOrder(m.categoryOrder).map((c) =>
          c === oldName ? trimmed : c
        );
        const nextOrder = toCategoryOrderTuples(orderNames);

        return {
          ...m,
          categoryOrder: nextOrder as unknown as Menu["categoryOrder"],
          menuPages: (m.menuPages || []).map((p) => ({
            ...p,
            menuItems: (p.menuItems || []).map((it) => {
              const sourceCats = getItemCategories(it);
              const enCats = getItemEnglishCategories(it);
              const sourceHit = sourceCats.includes(oldName);
              const enHit = enCats.includes(oldName);
              if (!sourceHit && !enHit) return it;

              let next: MenuItem = { ...it };
              if (sourceHit) {
                const nextSource = sourceCats.map((c) =>
                  c === oldName ? trimmed : c
                );
                next = {
                  ...next,
                  category: nextSource[0] ?? null,
                  categories: nextSource,
                } as MenuItem;
              }
              if (enHit) {
                const props = ((it as any).props || {}) as Record<string, any>;
                const translations = { ...(props.translations || {}) };
                const en = { ...(translations.en || {}) };
                const nextEnList = enCats.map((c) =>
                  c === oldName ? trimmed : c
                );
                en.categoryList = nextEnList;
                en.category = {
                  ...(en.category || {}),
                  text: nextEnList[0] ?? "",
                };
                translations.en = en;
                (next as any).props = { ...props, translations };
              }
              return next;
            }),
          })),
        };
      });
      try {
        await trackSave(() =>
          api.post(`/menus/${menuId}/categories/rename`, {
            oldCategoryName: oldName,
            newCategoryName: trimmed,
          })
        );
      } catch (e) {
        if (prev) queryClient.setQueryData(menuKey(menuId), prev);
        throw e;
      }
    },
    [menuId, patchMenu, queryClient, trackSave]
  );

  /**
   * Persist an ordered category list. Writes the tuple format
   * `[[name, idx], ...]` that the public menu and legacy editor expect.
   */
  const reorderCategories = useCallback(
    async (next: string[]) => {
      const tuples = toCategoryOrderTuples(next);
      await updateMenu({
        categoryOrder: tuples as unknown as Menu["categoryOrder"],
      });
    },
    [updateMenu]
  );

  return {
    menu: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    saveStatus,
    lastSavedAt,
    // Mutations
    updateMenu,
    updateItem,
    deleteItem,
    createItem,
    updatePage,
    renameCategory,
    reorderCategories,
    // Internal escape hatch for advanced flows
    patchMenu,
  };
}

export {
  menuKey,
  getItemCategories,
  getItemEnglishCategories,
  normalizeCategoryList,
  readCategoryOrder,
  toCategoryOrderTuples,
};
